import React, { useEffect } from 'react';
import { TextInput, required, number, minValue } from 'react-admin';
import { Button } from '@material-ui/core';
import { useForm, useField } from 'react-final-form';

type Props = {
    source: string;
    storageId?: number | string | null;
    storagesById: Record<string, any>;
};

const SMEQtyInput: React.FC<Props> = ({ source, storageId, storagesById }) => {
    const form = useForm();
    const {
        input: { value: currentQty },
    } = useField(source, { subscription: { value: true } });
    const storage = storageId ? storagesById?.[String(storageId)] : null;
    const isInventory = storage?.storagetype?.inventory;
    const isNonInventory = isInventory === false;
    const availableQty = Number(storage?.qty ?? storage?.storedQty ?? 0);
    const safeAvailableQty = Number.isNaN(availableQty)
        ? 0
        : Math.max(availableQty, 0);
    const safeCurrentQty = Number(currentQty || 0);
    const remainingQty = Math.max(safeAvailableQty - safeCurrentQty, 0);
    const qtyExceedsAvailable =
        !isNonInventory &&
        safeAvailableQty > 0 &&
        !Number.isNaN(safeCurrentQty) &&
        safeCurrentQty > safeAvailableQty;

    useEffect(() => {
        if (storageId && isNonInventory) {
            form.change(source, 1);
        }
    }, [form, isNonInventory, source, storageId]);

    useEffect(() => {
        const isEmptyQty =
            currentQty === undefined ||
            currentQty === null ||
            currentQty === '';
        if (storageId && !isNonInventory && isEmptyQty) {
            form.change(source, 0);
        }
    }, [currentQty, form, isNonInventory, source, storageId]);

    return (
        <TextInput
            source={source}
            label="Qty"
            disabled={isNonInventory}
            helperText={
                storageId
                    ? isNonInventory
                        ? 'Non-inventory item: qty is fixed to 1'
                        : `Available: ${safeAvailableQty} | Remaining: ${remainingQty}`
                    : 'Select item first'
            }
            validate={[
                required(),
                number(),
                minValue(1),
                value =>
                    !isNonInventory &&
                    safeAvailableQty > 0 &&
                    Number(value) > safeAvailableQty
                        ? `Qty cannot exceed available (${safeAvailableQty})`
                        : undefined,
            ]}
            InputProps={{
                endAdornment:
                    !isNonInventory && storageId ? (
                        <Button
                            size="small"
                            color="primary"
                            style={{ minWidth: 56 }}
                            onClick={e => {
                                e.preventDefault();
                                form.change(source, safeAvailableQty);
                            }}
                        >
                            Use Max
                        </Button>
                    ) : undefined,
            }}
            error={qtyExceedsAvailable}
        />
    );
};

export default SMEQtyInput;
